"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, LoaderCircle, Mail } from "lucide-react";
import { replaceDocument } from "@/components/app-navigation-link";
import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ApiError } from "@/lib/api-client";
import { login } from "@/lib/auth-api";
import { safeAuthDestination } from "@/lib/auth-redirect";
import { useQueryClient } from "@tanstack/react-query";

const invalidCredentialsMessage = "E-mail ou senha inválidos.";
const genericLoginMessage = "Não foi possível entrar agora. Tente novamente em instantes.";

const loginSchema = z.object({
  email: z.string().trim().email("Informe um e-mail válido."),
  password: z.string().min(1, "Informe a senha."),
});

type LoginFormValues = z.infer<typeof loginSchema>;

type LoginFormProps = {
  initialMessage?: string;
  isSessionLoading?: boolean;
  redirectTo?: string;
};

export function LoginForm({
  initialMessage,
  isSessionLoading = false,
  redirectTo = "/whatsapp",
}: LoginFormProps) {
  const queryClient = useQueryClient();
  const alertRef = React.useRef<HTMLParagraphElement>(null);
  const [formMessage, setFormMessage] = React.useState(initialMessage ?? "");
  const [showPassword, setShowPassword] = React.useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const isSubmitting = form.formState.isSubmitting;
  const isDisabled = isSubmitting || isSessionLoading;

  React.useEffect(() => {
    if (formMessage) {
      alertRef.current?.focus();
    }
  }, [formMessage]);

  async function onSubmit(values: LoginFormValues) {
    setFormMessage("");

    try {
      await login(values);
      queryClient.clear();
      replaceDocument(safeAuthDestination(redirectTo));
    } catch (error) {
      form.resetField("password");

      if (error instanceof ApiError && error.code === "AUTH_INVALID_CREDENTIALS") {
        setFormMessage(invalidCredentialsMessage);
        return;
      }

      setFormMessage(genericLoginMessage);
    }
  }

  return (
    <Form {...form}>
      <form className="space-y-5" noValidate onSubmit={form.handleSubmit(onSubmit)}>
        {formMessage ? (
          <p
            ref={alertRef}
            role="alert"
            tabIndex={-1}
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive outline-none focus-visible:ring-3 focus-visible:ring-destructive/20"
          >
            {formMessage}
          </p>
        ) : null}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>E-mail</FormLabel>
              <div className="relative">
                <Mail aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3.25 size-4 -translate-y-1/2 text-muted-foreground" />
                <FormControl><Input className="h-11 bg-background pl-10" autoComplete="email" disabled={isDisabled} inputMode="email" placeholder="nome@empresa.com" type="email" {...field} /></FormControl>
              </div>
              <FormMessage role="alert" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Senha</FormLabel>
              <div className="relative">
                <FormControl>
                  <Input
                    className="h-11 bg-background pr-12"
                    autoComplete="current-password"
                    disabled={isDisabled}
                    type={showPassword ? "text" : "password"}
                    {...field}
                  />
                </FormControl>
                <Button
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  className="absolute inset-y-0 right-0 h-11 w-11 rounded-l-none text-muted-foreground hover:translate-y-0 hover:bg-muted hover:text-foreground hover:shadow-none active:translate-y-0"
                  disabled={isDisabled}
                  type="button"
                  variant="ghost"
                  onClick={() => setShowPassword((current) => !current)}
                >
                  {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                </Button>
              </div>
              <FormMessage role="alert" />
            </FormItem>
          )}
        />

        <Button className="h-11 w-full" disabled={isDisabled} type="submit">
          {isSubmitting ? <LoaderCircle aria-hidden="true" className="animate-spin" /> : null}
          Entrar
        </Button>
      </form>
    </Form>
  );
}
